import { TelemetryEventType } from './telemetry-models';
import { BaseTracker } from './base-tracker';
import type { ErrorEvent as TelemetryErrorEvent, TelemetryEvent } from './telemetry-models';

/**
 * Tracks JavaScript errors and unhandled promise rejections with cross-browser compatibility
 */
export class ErrorTracker extends BaseTracker {
  private _appOriginPatterns?: string[];
  private _navigationType: 'navigate' | 'reload' | 'back_forward' | 'prerender';

  // Known CDN patterns that should be marked as third-party
  private static readonly CDN_PATTERNS = [
    'cdn.jsdelivr.net',
    'unpkg.com',
    'cdnjs.cloudflare.com',
    'cdn.jsdelivr.net',
    'ajax.googleapis.com',
    'code.jquery.com',
    'stackpath.bootstrapcdn.com',
    'maxcdn.bootstrapcdn.com',
  ];

  // Known browser extension URL patterns
  private static readonly EXTENSION_PATTERNS = [
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    'edge-extension://',
  ];

  constructor(
    onEvent: (event: TelemetryEvent) => void,
    navigationType: 'navigate' | 'reload' | 'back_forward' | 'prerender',
    appOriginPatterns?: string[],
    enabledLogging = false
  ) {
    super(onEvent, enabledLogging);
    this._navigationType = navigationType;
    this._appOriginPatterns = appOriginPatterns;
  }

  /**
   * Start tracking errors
   */
  public start(): void {
    window.addEventListener('error', this._handleError);
    window.addEventListener('unhandledrejection', this._handleRejection);
    this._log('Error tracking started');
  }

  /**
   * Stop tracking errors
   */
  public stop(): void {
    window.removeEventListener('error', this._handleError);
    window.removeEventListener('unhandledrejection', this._handleRejection);
    this._log('Error tracking stopped');
  }

  private _handleError = (event: Event): void => {
    try {
      // Type guard to ensure we have an ErrorEvent
      if (!this._isErrorEvent(event)) {
        this._log('Received non-ErrorEvent in error handler');
        return;
      }

      // After type guard check, TypeScript knows event is ErrorEvent
      // Extract error information with safe access
      const message = this._normalizeErrorMessage(event.message);
      const stack = this._safeSerializeStack(event.error);
      const url = this._sanitizeUrl(event.filename || window.location.href);
      const line = this._normalizeLineNumber(event.lineno);
      const column = this._normalizeColumnNumber(event.colno);

      const errorData: TelemetryErrorEvent = {
        message,
        stack,
        url,
        line,
        column,
        is_third_party: this._isThirdPartyError(event.filename),
        timestamp: Date.now(),
        user_agent: navigator.userAgent,
        navigation_type: this._navigationType,
      };

      this._onEvent({ type: TelemetryEventType.ERROR, data: errorData });
    } catch (error) {
      // Never throw in error handler - log if enabled
      this._log('Error in _handleError:', error);
    }
  };

  private _handleRejection = (event: Event): void => {
    try {
      // Type guard to ensure we have a PromiseRejectionEvent
      if (!this._isPromiseRejectionEvent(event)) {
        this._log('Received non-PromiseRejectionEvent in rejection handler');
        return;
      }

      const rejectionEvent = event as PromiseRejectionEvent;
      const reason = rejectionEvent.reason;

      // Extract message and stack from various rejection types
      let message: string;
      let stack: string | undefined;

      if (reason instanceof Error) {
        message = this._normalizeErrorMessage(reason.message);
        stack = this._safeSerializeStack(reason);
      } else if (typeof reason === 'string') {
        message = this._normalizeErrorMessage(reason);
      } else if (reason === null || reason === undefined) {
        message = String(reason);
      } else {
        // For objects, numbers, etc., convert to string
        message = String(reason);
      }

      const errorData: TelemetryErrorEvent = {
        message,
        stack,
        url: this._sanitizeUrl(window.location.href),
        is_third_party: false, // Promise rejections are always considered app errors
        timestamp: Date.now(),
        user_agent: navigator.userAgent,
        navigation_type: this._navigationType,
      };

      this._onEvent({ type: TelemetryEventType.ERROR, data: errorData });
    } catch (error) {
      // Never throw in error handler - log if enabled
      this._log('Error in _handleRejection:', error);
    }
  };

  private _isThirdPartyError(filename?: string): boolean {
    if (!filename) return false;

    // Check for browser extension URLs
    if (ErrorTracker.EXTENSION_PATTERNS.some((pattern) => filename.startsWith(pattern))) {
      return true;
    }

    // Check for known CDN patterns
    if (ErrorTracker.CDN_PATTERNS.some((pattern) => filename.includes(pattern))) {
      return true;
    }

    const origin = window.location.origin;
    if (filename.startsWith(origin)) return false;

    // Check against configured app origin patterns
    if (this._appOriginPatterns) {
      return !this._appOriginPatterns.some((pattern) => filename.includes(pattern));
    }

    return true;
  }

  /**
   * Type guard to check if event is an ErrorEvent
   */
  private _isErrorEvent(event: Event): event is globalThis.ErrorEvent {
    return 'message' in event && 'filename' in event;
  }

  /**
   * Type guard to check if event is a PromiseRejectionEvent
   */
  private _isPromiseRejectionEvent(event: Event): event is PromiseRejectionEvent {
    return 'reason' in event && 'promise' in event;
  }

  /**
   * Safely serialize error stack trace, handling circular references
   */
  private _safeSerializeStack(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') {
      return undefined;
    }

    try {
      // Try to access stack property
      const stack = (error as { stack?: unknown }).stack;
      if (typeof stack === 'string') {
        // Limit stack trace length to prevent excessive data
        const maxLength = 10000;
        return stack.length > maxLength ? stack.substring(0, maxLength) + '...[truncated]' : stack;
      }
      return undefined;
    } catch (e) {
      // Stack access might throw in some edge cases
      this._log('Failed to access error stack:', e);
      return undefined;
    }
  }

  /**
   * Normalize error message, handling cross-origin "Script error" and privacy concerns
   */
  private _normalizeErrorMessage(message: string): string {
    if (!message || typeof message !== 'string') {
      return 'Unknown error';
    }

    // Detect cross-origin "Script error" - common in CORS-blocked scripts
    if (message === 'Script error.' || message === 'Script error') {
      return 'Script error (cross-origin)';
    }

    // Filter potential PII from error messages
    return this._filterPII(message);
  }

  /**
   * Normalize line number (handle undefined, 0, and negative values)
   */
  private _normalizeLineNumber(line: number | undefined): number | undefined {
    if (line === undefined || line === null || line === 0) {
      return undefined;
    }
    return line > 0 ? line : undefined;
  }

  /**
   * Normalize column number (handle undefined, 0, and negative values)
   */
  private _normalizeColumnNumber(column: number | undefined): number | undefined {
    if (column === undefined || column === null || column === 0) {
      return undefined;
    }
    return column > 0 ? column : undefined;
  }

  /**
   * Sanitize URLs to remove sensitive query parameters
   */
  private _sanitizeUrl(url: string): string {
    if (!url || typeof url !== 'string') {
      return window.location.href;
    }

    try {
      const urlObj = new URL(url);

      // Remove potentially sensitive query parameters
      const sensitiveParams = [
        'token',
        'access_token',
        'refresh_token',
        'api_key',
        'apikey',
        'key',
        'secret',
        'password',
        'pwd',
        'session',
        'sessionid',
        'auth',
        'authorization',
      ];

      sensitiveParams.forEach((param) => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]');
        }
      });

      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, return original (might not be a valid URL)
      return url;
    }
  }

  /**
   * Filter personally identifiable information from error messages
   */
  private _filterPII(message: string): string {
    let filtered = message;

    // Redact email addresses
    filtered = filtered.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL_REDACTED]');

    // Redact phone numbers (various formats)
    filtered = filtered.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[PHONE_REDACTED]');
    filtered = filtered.replace(/\b\+\d{1,3}[-.\s]?\d{1,14}\b/g, '[PHONE_REDACTED]');

    // Redact credit card numbers
    filtered = filtered.replace(/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, '[CARD_REDACTED]');

    // Redact SSN patterns
    filtered = filtered.replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN_REDACTED]');

    // Redact IP addresses
    filtered = filtered.replace(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, '[IP_REDACTED]');

    // Redact JWT tokens (basic pattern)
    filtered = filtered.replace(/eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g, '[JWT_REDACTED]');

    return filtered;
  }
}
